/**
 * FS-501 — rewriting a saved Device List view after the server resolved its
 * tag filter through the merge alias table.
 *
 * Tag filters store tag IDs so a rename cannot break them, but a merge retires
 * an ID. The server resolves it for the request in flight; this makes the fix
 * permanent in the stored filter, so the view heals once instead of leaning on
 * the server to re-resolve on every load.
 *
 * A retired ID left in place would fail asymmetrically — `tags` matches
 * nothing, which is obvious, while `not_tags` excludes nothing and silently
 * un-hides the group the view existed to hide.
 */
export interface TagFilterChange {
  /** Old tag id -> the id it was merged into. */
  remapped: Record<string, string>;
  /** Old tag ids whose tag was deleted outright, with no successor. */
  gone: string[];
}

/**
 * The filter's new comma-joined value. Returns '' when every value resolved
 * away, which the caller treats as "remove the filter" — leaving `not_tags`
 * present but valueless would be the silent-permissive bug again.
 */
export function healTagFilterValue(current: string, change: TagFilterChange): string {
  return [
    ...new Set(
      current
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v && !change.gone.includes(v))
        .map((v) => change.remapped[v] ?? v),
    ),
  ].join(',');
}
